'use client';
import React from 'react';
import Modal from '@/components/ui/Modal';
import Icon from '@/components/ui/AppIcon';
import { StockCount } from './StockCountContent';
import { toast } from 'sonner';

interface Props {
  open: boolean;
  count: StockCount;
  onClose: () => void;
}

export default function NonVarianceReportModal({ open, count, onClose }: Props) {
  const matchedItems = count.lineItems.filter((li) => (li.variance ?? 0) === 0 && li.physicalQty !== null);
  const varianceItems = count.lineItems.filter((li) => (li.variance ?? 0) !== 0);
  const totalVarianceValue = count.lineItems.reduce((acc, li) => acc + (li.varianceValue ?? 0), 0);
  const matchRate = count.lineItems.length > 0
    ? Math.round((matchedItems.length / count.lineItems.length) * 100)
    : 0;

  const generatedAt = new Date().toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Non-Variance Reconciliation Report"
      subtitle={`RPT-003 · ${count.countNumber} · ${count.site}`}
      size="2xl"
      footer={
        <div className="flex items-center justify-between gap-4">
          <p className="text-xs text-muted-foreground">Generated: {generatedAt} · By: Arjun Mehta</p>
          <div className="flex gap-3">
            <button
              onClick={() => { toast.success('Report exported as PDF.'); }}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-600 hover:bg-primary/90 transition-all active:scale-95"
            >
              <Icon name="DocumentArrowDownIcon" size={15} />
              Export PDF
            </button>
            <button
              onClick={() => { toast.success('Report exported as Excel.'); }}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-600 hover:bg-teal-700 transition-all active:scale-95"
            >
              <Icon name="TableCellsIcon" size={15} />
              Export Excel
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Report header block */}
        <div className="bg-primary rounded-xl p-5 text-white">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-500 text-white/60 uppercase tracking-widest mb-1">Opsivo · Non-Variance Reconciliation Report</p>
              <h2 className="text-xl font-700">RPT-003 — Stock Count Reconciliation</h2>
              <p className="text-sm text-white/70 mt-1">{count.countNumber} · {count.site} · Count Date: {count.countDate}</p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-xs text-white/60">Generated</p>
              <p className="text-sm font-600">{generatedAt}</p>
              <p className="text-xs text-white/60 mt-1">Status</p>
              <span className={`text-xs font-700 px-2 py-0.5 rounded-full ${
                count.status === 'APPROVED' ? 'bg-teal-400/20 text-teal-200' : 'bg-red-400/20 text-red-200'
              }`}>
                {count.status}
              </span>
            </div>
          </div>
        </div>

        {/* Reconciliation summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Total Items Counted', value: count.lineItems.length, icon: 'ClipboardDocumentListIcon', color: 'text-foreground', bg: 'bg-muted/50' },
            { label: 'Matched (No Variance)', value: matchedItems.length, icon: 'CheckCircleIcon', color: 'text-teal-600', bg: 'bg-teal-50 border-teal-200' },
            { label: 'With Variance', value: varianceItems.length, icon: 'ExclamationTriangleIcon', color: varianceItems.length > 0 ? 'text-red-600' : 'text-teal-600', bg: varianceItems.length > 0 ? 'bg-red-50 border-red-200' : 'bg-teal-50 border-teal-200' },
            { label: 'Match Rate', value: `${matchRate}%`, icon: 'ChartBarIcon', color: matchRate >= 90 ? 'text-teal-600' : matchRate >= 70 ? 'text-amber-600' : 'text-red-600', bg: 'bg-muted/50' },
          ].map((s) => (
            <div key={`rpt-sum-${s.label}`} className={`border rounded-xl p-4 text-center ${s.bg}`}>
              <Icon name={s.icon as Parameters<typeof Icon>[0]['name']} size={20} className={`mx-auto mb-1 ${s.color}`} />
              <p className={`text-2xl font-700 ${s.color}`}>{s.value}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Reconciliation status bar */}
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-600 text-foreground">Reconciliation Progress</span>
            <span className="text-xs font-700 text-foreground">{matchRate}% matched</span>
          </div>
          <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${matchRate >= 90 ? 'bg-teal-500' : matchRate >= 70 ? 'bg-amber-500' : 'bg-red-500'}`}
              style={{ width: `${matchRate}%` }}
            />
          </div>
          <div className="flex justify-between mt-1.5">
            <span className="text-[10px] text-teal-600 font-500">{matchedItems.length} matched</span>
            <span className="text-[10px] text-red-600 font-500">{varianceItems.length} variance</span>
          </div>
        </div>

        {/* Non-variance items (the "clean" reconciliation list) */}
        <div>
          <h3 className="text-sm font-600 text-foreground mb-3 flex items-center gap-2">
            <Icon name="CheckCircleIcon" size={15} className="text-teal-600" />
            Reconciled Items — No Variance
            <span className="text-xs text-muted-foreground font-400">(These items are fully reconciled — physical count matches system balance)</span>
          </h3>
          {matchedItems.length === 0 ? (
            <div className="bg-muted/30 border border-border rounded-xl p-6 text-center text-muted-foreground text-sm">
              No items with zero variance in this count.
            </div>
          ) : (
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-primary text-white">
                    <th className="px-3 py-2.5 text-left font-600">#</th>
                    <th className="px-3 py-2.5 text-left font-600">Item Code</th>
                    <th className="px-3 py-2.5 text-left font-600">Item Name</th>
                    <th className="px-3 py-2.5 text-left font-600">Category</th>
                    <th className="px-3 py-2.5 text-center font-600">System Qty</th>
                    <th className="px-3 py-2.5 text-center font-600">Physical Qty</th>
                    <th className="px-3 py-2.5 text-right font-600">Unit Cost</th>
                    <th className="px-3 py-2.5 text-right font-600">Stock Value</th>
                    <th className="px-3 py-2.5 text-center font-600">Reconciled</th>
                  </tr>
                </thead>
                <tbody>
                  {matchedItems.map((li, i) => (
                    <tr key={`rpt-match-${li.id}`} className={`border-t border-border ${i % 2 === 0 ? 'bg-card' : 'bg-muted/20'}`}>
                      <td className="px-3 py-2.5 text-muted-foreground">{i + 1}</td>
                      <td className="px-3 py-2.5 font-600 text-secondary">{li.itemCode}</td>
                      <td className="px-3 py-2.5 font-500">{li.itemName}</td>
                      <td className="px-3 py-2.5 text-muted-foreground">{li.category.replace('_', ' ')}</td>
                      <td className="px-3 py-2.5 text-center font-500">{li.systemQty} {li.uom}</td>
                      <td className="px-3 py-2.5 text-center font-600 text-teal-700">{li.physicalQty} {li.uom}</td>
                      <td className="px-3 py-2.5 text-right">€{li.unitCost.toFixed(2)}</td>
                      <td className="px-3 py-2.5 text-right font-600">€{(li.systemQty * li.unitCost).toFixed(2)}</td>
                      <td className="px-3 py-2.5 text-center">
                        <span className="inline-flex items-center gap-1 text-[10px] font-600 text-teal-700 bg-teal-50 border border-teal-200 px-2 py-0.5 rounded-full">
                          <Icon name="CheckIcon" size={10} /> ✓
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-teal-50 border-t-2 border-teal-200">
                    <td colSpan={7} className="px-3 py-2.5 text-xs font-700 text-teal-800">Total Reconciled Value</td>
                    <td className="px-3 py-2.5 text-right font-700 text-teal-800">
                      €{matchedItems.reduce((acc, li) => acc + li.systemQty * li.unitCost, 0).toFixed(2)}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* Variance items section */}
        {varianceItems.length > 0 && (
          <div>
            <h3 className="text-sm font-600 text-foreground mb-3 flex items-center gap-2">
              <Icon name="ExclamationTriangleIcon" size={15} className="text-amber-500" />
              Items with Variance
              <span className="text-xs text-muted-foreground font-400">(Adjustments {count.adjustmentPosted ? 'have been posted' : 'pending approval'})</span>
            </h3>
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-amber-600 text-white">
                    <th className="px-3 py-2.5 text-left font-600">Item Code</th>
                    <th className="px-3 py-2.5 text-left font-600">Item Name</th>
                    <th className="px-3 py-2.5 text-center font-600">System Qty</th>
                    <th className="px-3 py-2.5 text-center font-600">Physical Qty</th>
                    <th className="px-3 py-2.5 text-center font-600">Variance</th>
                    <th className="px-3 py-2.5 text-center font-600">Variance %</th>
                    <th className="px-3 py-2.5 text-right font-600">Value Impact</th>
                    <th className="px-3 py-2.5 text-left font-600">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {varianceItems.map((li, i) => (
                    <tr key={`rpt-var-${li.id}`} className={`border-t border-border ${i % 2 === 0 ? 'bg-card' : 'bg-amber-50/30'}`}>
                      <td className="px-3 py-2.5 font-600 text-secondary">{li.itemCode}</td>
                      <td className="px-3 py-2.5">{li.itemName}</td>
                      <td className="px-3 py-2.5 text-center">{li.systemQty} {li.uom}</td>
                      <td className="px-3 py-2.5 text-center font-600">{li.physicalQty} {li.uom}</td>
                      <td className="px-3 py-2.5 text-center">
                        <span className={`font-700 ${(li.variance ?? 0) < 0 ? 'text-red-600' : 'text-teal-600'}`}>
                          {(li.variance ?? 0) > 0 ? '+' : ''}{li.variance} {li.uom}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <span className={`font-600 ${(li.variancePct ?? 0) < 0 ? 'text-red-600' : 'text-teal-600'}`}>
                          {(li.variancePct ?? 0) > 0 ? '+' : ''}{li.variancePct?.toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <span className={`font-700 ${(li.varianceValue ?? 0) < 0 ? 'text-red-600' : 'text-teal-600'}`}>
                          {(li.varianceValue ?? 0) >= 0 ? '+' : ''}€{li.varianceValue?.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-muted-foreground italic">{li.notes || '—'}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-amber-100 border-t-2 border-amber-300">
                    <td colSpan={6} className="px-3 py-2.5 text-xs font-700 text-amber-800">Net Variance Value</td>
                    <td className={`px-3 py-2.5 text-right font-700 ${totalVarianceValue < 0 ? 'text-red-700' : 'text-teal-700'}`}>
                      {totalVarianceValue >= 0 ? '+' : ''}€{totalVarianceValue.toFixed(2)}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* Adjustment status */}
        <div className={`flex items-start gap-3 p-4 rounded-xl border ${
          count.adjustmentPosted
            ? 'bg-teal-50 border-teal-200'
            : count.status === 'REJECTED' ?'bg-red-50 border-red-200' :'bg-amber-50 border-amber-200'
        }`}>
          <Icon
            name={count.adjustmentPosted ? 'CheckCircleIcon' : count.status === 'REJECTED' ? 'XCircleIcon' : 'ClockIcon'}
            size={18}
            className={count.adjustmentPosted ? 'text-teal-600' : count.status === 'REJECTED' ? 'text-red-600' : 'text-amber-600'}
          />
          <div>
            <p className={`text-sm font-600 ${count.adjustmentPosted ? 'text-teal-800' : count.status === 'REJECTED' ? 'text-red-800' : 'text-amber-800'}`}>
              {count.adjustmentPosted
                ? `Adjustments Posted — Approved by ${count.approvedBy} on ${count.approvedDate}`
                : count.status === 'REJECTED'
                ? `Count Rejected — ${count.rejectionReason}`
                : 'Adjustments Pending — Awaiting manager approval to post to inventory'}
            </p>
            {count.adjustmentPosted && varianceItems.length > 0 && (
              <p className="text-xs text-teal-700 mt-1">
                {varianceItems.length} ADJ transaction(s) posted. Inventory balances updated.
              </p>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
